import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Button } from "@material-ui/core";
import { useSnackbar } from "notistack";
import Auth from "@aws-amplify/auth";
import { PasswordTextField } from "components";
import config from "config";
import i18n from "./i18n";

// prop types
interface ChangePasswordDialogProps {
    isAlias: boolean,
    show: boolean,
    onClose: () => void,
}

// change password dialog
const ChangePasswordDialog = ({ isAlias, show, onClose }: ChangePasswordDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();

    // ui state
    const [error, setError] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
    const [oldPassword, setOldPassword] = useState("");

    // did hide hook -> clear input fields
    useEffect(() => {
        if (!show) {
            setError("");
            setNewPassword("");
            setNewPasswordConfirm("");
            setOldPassword("");
        }
    }, [show]);

    // execute action
    const execute = async () => {
        setError("");
        setLoading(true);
        try {
            if (isAlias && !config.aliasAccount.pinRegex.test(newPassword)) throw { type: "pinPolicy" }; // eslint-disable-line
            if (!isAlias && (newPassword.length < 6 || !/[A-Z]/.test(newPassword))) throw { type: "passwordPolicy" }; // eslint-disable-line
            if (newPassword !== newPasswordConfirm) throw { type: "passwordsDontMatch" }; // eslint-disable-line

            const user = await Auth.currentAuthenticatedUser();
            if (isAlias) {
                await Auth.changePassword(user, `${oldPassword}${config.aliasAccount.passwordSuffix}`, `${newPassword}${config.aliasAccount.passwordSuffix}`);
            } else {
                await Auth.changePassword(user, oldPassword, newPassword);
            }
            enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
            onClose();
        } catch (err: any) {
            setError(err.type || err.name);
        }
        setLoading(false);
    };

    // render
    return (
        <Dialog open={show} onClose={onClose}>
            <DialogTitle>{i18n.changePassword(isAlias)}</DialogTitle>
            <DialogContent dividers>

                {/* old password */}
                <PasswordTextField
                    autoFocus
                    disabled={isLoading}
                    error={["InvalidParameterException", "NotAuthorizedException"].includes(error)}
                    helperText={(["InvalidParameterException", "NotAuthorizedException"].includes(error)) && i18n.changePasswordErrors[error]}
                    label={i18n.currentPassword(isAlias)}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                />

                {/* new password */}
                <PasswordTextField
                    disabled={isLoading}
                    error={error === "passwordPolicy" || error === "pinPolicy"}
                    helperText={(error === "passwordPolicy" || error === "pinPolicy") && i18n.changePasswordErrors[error]}
                    label={i18n.newPassword(isAlias)}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                />

                {/* confirm new password */}
                <PasswordTextField
                    disabled={isLoading}
                    error={error === "passwordsDontMatch"}
                    helperText={(error === "passwordsDontMatch") && i18n.changePasswordErrors[error]}
                    label={i18n.newPasswordConfirm(isAlias)}
                    value={newPasswordConfirm}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                />

            </DialogContent>
            {isLoading && <LinearProgress />}
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    {i18n.cancel}
                </Button>
                <Button color="primary" onClick={execute} disabled={isLoading}>
                    {i18n.ok}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangePasswordDialog;
